export { DashboardPage } from './DashboardPage';
export { ReportsPage } from './ReportsPage';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { localizeLabel, resolveStatus } from '@/lib/constants';
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, Input, PageHeader, Pagination, Skeleton, SkeletonCard, SkeletonRow, SkeletonText, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, DropdownSelect, useConfirm } from '@/components/ui';

type Contact = { id: string; name: string; email: string; phone?: string | null; company?: string | null; timezone?: string | null; createdAt?: string };
type TicketSummary = { id: string; ticketNumber: number; subject: string; status: string; createdAt: string };
type Company = { id: string; name: string; domain?: string | null; description?: string | null; _count?: { users: number; tickets: number }; users?: Array<{ id: string; firstName: string; lastName: string; email: string; isActive: boolean }>; tickets?: TicketSummary[] };

type Translator = ReturnType<typeof useI18n>['t'];

function errorMessage(error: unknown, t: Translator) {
  return error instanceof ApiError ? error.message : t('dir.loadError');
}

function PageError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <ErrorState message={message} onRetry={onRetry} />;
}

/** CONTACT-02: format a date string in the contact's configured timezone. */
function formatInTz(dateStr: string, language: string, timezone?: string | null): string {
  try {
    return new Intl.DateTimeFormat(language, {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(dateStr));
  } catch {
    return new Date(dateStr).toLocaleDateString(language);
  }
}

export function ContactsPage() {
  const { t } = useI18n();
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(50);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get<{ data: Contact[]; meta: { page: number; pageSize: number; total: number } }>('/contacts', { params: { search: search || undefined, page, pageSize } });
      setContacts(res.data); setTotal(res.meta.total);
    }
    catch (e) { setError(errorMessage(e, t)); }
    finally { setLoading(false); }
  }, [search, page, pageSize, t]);
  React.useEffect(() => { const id = window.setTimeout(load, 250); return () => window.clearTimeout(id); }, [load]);
  // Reset to page 1 when search changes
  const prevSearch = React.useRef(search);
  React.useEffect(() => { if (prevSearch.current !== search) { setPage(1); prevSearch.current = search; } }, [search]);

  return <div className="space-y-6">
    <PageHeader className="mb-0" title={t('nav.contacts')} description={t('dir.contacts.description')} />
    <Card><CardContent className="p-4"><Input aria-label={t('dir.contacts.searchAria')} placeholder={t('agents.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} /></CardContent></Card>
    {error ? <PageError message={error} onRetry={load} /> : <Card><CardHeader><CardTitle>{loading ? t('nav.contacts') : t('dir.contacts.count', { count: total })}</CardTitle></CardHeader><CardContent className="p-0">
      {loading ? <SkeletonRow rows={5} /> : contacts.length === 0 ? <EmptyState title={search.trim() ? t('dir.contacts.noMatch') : t('dir.contacts.none')} description={search.trim() ? t('agents.tryDifferent') : t('dir.contacts.noneDescription')} /> : <><Table><TableHeader><TableRow><TableHead>{t('common.name')}</TableHead><TableHead>{t('agents.email')}</TableHead><TableHead>{t('dir.company')}</TableHead><TableHead>{t('agents.phone')}</TableHead></TableRow></TableHeader><TableBody>{contacts.map((contact) => <TableRow key={contact.id}><TableCell><Link className="font-medium text-brand-700 hover:underline" to={`/contacts/${contact.id}`}>{contact.name || t('dir.unnamedContact')}</Link></TableCell><TableCell>{contact.email}</TableCell><TableCell>{contact.company || '—'}</TableCell><TableCell>{contact.phone || '—'}</TableCell></TableRow>)}</TableBody></Table><div className="p-4"><Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} /></div></>}
    </CardContent></Card>}
  </div>;
}

export function ContactDetailPage() {
  const { t, tOr, language } = useI18n();
  const { confirm } = useConfirm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = React.useState<Contact & { tickets?: TicketSummary[] } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({ firstName: '', lastName: '', phone: '', timezone: '' });

  const load = React.useCallback(async () => {
    if (!id) return;
    setLoading(true); setError('');
    try { setContact(await api.get<typeof contact>(`/contacts/${id}`)); }
    catch (e) { setError(errorMessage(e, t)); }
    finally { setLoading(false); }
  }, [id, t]);

  React.useEffect(() => { void load(); }, [load]);

  function startEdit() {
    if (!contact) return;
    const [first, ...rest] = (contact.name || '').split(' ');
    setForm({ firstName: first || '', lastName: rest.join(' '), phone: contact.phone || '', timezone: contact.timezone || '' });
    setEditing(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true); setError('');
    try {
      await api.put(`/contacts/${id}`, { firstName: form.firstName, lastName: form.lastName, phone: form.phone || undefined, timezone: form.timezone || undefined });
      setEditing(false);
      await load();
    } catch (e) { setError(errorMessage(e, t)); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!id || !await confirm({ title: t('dir.confirmDeleteContactTitle', { name: contact?.name ?? t('dir.thisContact') }), description: t('dir.confirmDeleteContactDescription'), confirmLabel: t('dir.deleteContact') })) return;
    try { await api.delete(`/contacts/${id}`); navigate('/contacts'); }
    catch (e) { setError(errorMessage(e, t)); }
  }

  if (loading) return <div className="space-y-6"><SkeletonText lines={2} className="max-w-sm" /><div className="grid gap-6 lg:grid-cols-3"><Card><CardContent className="p-6"><SkeletonText lines={4} /></CardContent></Card><Card className="lg:col-span-2"><CardContent className="p-0"><SkeletonRow rows={3} /></CardContent></Card></div></div>;
  if (error || !contact) return <PageError message={error || t('dir.contactNotFound')} onRetry={load} />;

  return <div className="space-y-6">
    <PageHeader
      className="mb-0"
      breadcrumb={[{ label: t('nav.contacts'), to: '/contacts' }, { label: contact.name || t('dir.contact') }]}
      title={contact.name || t('dir.contact')}
      actions={(
        <>
          {!editing && <Button variant="outline" onClick={startEdit}>{t('agents.edit')}</Button>}
          <Button variant="destructive" onClick={() => void handleDelete()}>{t('common.delete')}</Button>
        </>
      )}
    />
    <div className="grid gap-6 lg:grid-cols-3">
      <Card>
        <CardHeader><CardTitle>{editing ? t('dir.editProfile') : t('dir.profile')}</CardTitle></CardHeader>
        <CardContent>
          {editing ? (
            <form className="space-y-3" onSubmit={handleSave}>
              <Input label={t('dir.firstName')} value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} required />
              <Input label={t('dir.lastName')} value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
              <Input label={t('dir.phone')} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              <Input label={t('dir.timezone')} value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} />
              {error && <p className="text-sm text-danger-600">{error}</p>}
              <div className="flex gap-2">
                <Button type="submit" disabled={saving || !form.firstName.trim()}>{saving ? t('common.saving') : t('common.save')}</Button>
                <Button type="button" variant="outline" onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3 text-sm">
              <p><span className="font-medium">{t('dir.emailLabel')}</span> {contact.email}</p>
              <p><span className="font-medium">{t('dir.phoneLabel')}</span> {contact.phone || '—'}</p>
              <p><span className="font-medium">{t('dir.companyLabel')}</span> {contact.company || '—'}</p>
              <p><span className="font-medium">{t('dir.timezoneLabel')}</span> {contact.timezone || '—'}</p>
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardHeader><CardTitle>{t('dir.recentTickets')}</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!contact.tickets?.length ? <EmptyState title={t('dir.noTicketsYet')} description={t('dir.contactTicketsDescription')} /> : <Table><TableHeader><TableRow><TableHead>{t('dir.ticket')}</TableHead><TableHead>{t('dir.subject')}</TableHead><TableHead>{t('dir.status')}</TableHead><TableHead>{t('dir.created')}</TableHead></TableRow></TableHeader><TableBody>{contact.tickets.map((ticket) => <TableRow key={ticket.id}><TableCell><Link className="text-brand-700 hover:underline" to={`/tickets/${ticket.id}`}>#{ticket.ticketNumber}</Link></TableCell><TableCell>{ticket.subject}</TableCell><TableCell className="capitalize">{localizeLabel('status', ticket.status, resolveStatus(ticket.status).label, tOr)}</TableCell><TableCell className="text-xs text-ink-subtle">{formatInTz(ticket.createdAt, language, contact.timezone)}</TableCell></TableRow>)}</TableBody></Table>}
        </CardContent>
      </Card>
    </div>
  </div>;
}

export function CompaniesPage() {
  const { t } = useI18n();
  const [companies, setCompanies] = React.useState<Company[]>([]); const [search, setSearch] = React.useState(''); const [name, setName] = React.useState(''); const [domain, setDomain] = React.useState(''); const [loading, setLoading] = React.useState(true); const [saving, setSaving] = React.useState(false); const [error, setError] = React.useState('');
  const load = React.useCallback(async () => { setLoading(true); setError(''); try { setCompanies(await api.get<Company[]>('/companies', { params: { search: search || undefined } })); } catch (e) { setError(errorMessage(e, t)); } finally { setLoading(false); } }, [search, t]);
  React.useEffect(() => { const id = window.setTimeout(load, 250); return () => window.clearTimeout(id); }, [load]);
  async function createCompany(e: React.FormEvent) { e.preventDefault(); if (!name.trim()) return; setSaving(true); setError(''); try { await api.post('/companies', { name: name.trim(), domain: domain.trim() || undefined }); setName(''); setDomain(''); await load(); } catch (e) { setError(errorMessage(e, t)); } finally { setSaving(false); } }
  return <div className="space-y-6"><PageHeader className="mb-0" title={t('nav.companies')} description={t('dir.companies.description')} /><div className="grid gap-6 lg:grid-cols-3"><Card><CardHeader><CardTitle>{t('dir.companies.add')}</CardTitle></CardHeader><CardContent><form className="space-y-3" onSubmit={createCompany}><Input label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} required /><Input label={t('dir.companies.domain')} placeholder="example.com" value={domain} onChange={(e) => setDomain(e.target.value)} /><Button type="submit" disabled={saving || !name.trim()}>{saving ? t('common.saving') : t('dir.companies.create')}</Button></form></CardContent></Card><Card className="lg:col-span-2"><CardContent className="space-y-4 p-4"><Input aria-label={t('dir.companies.searchAria')} placeholder={t('dir.companies.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />{error && <ErrorState message={error} onRetry={() => void load()} />}{loading ? <SkeletonRow rows={4} /> : companies.length === 0 ? <EmptyState title={search.trim() ? t('dir.companies.noMatch') : t('dir.companies.addFirst')} description={search.trim() ? t('dir.companies.tryDifferent') : t('dir.companies.useForm')} /> : <Table><TableHeader><TableRow><TableHead>{t('common.name')}</TableHead><TableHead>{t('dir.companies.domain')}</TableHead><TableHead>{t('dir.companies.contacts')}</TableHead><TableHead>{t('dir.companies.tickets')}</TableHead></TableRow></TableHeader><TableBody>{companies.map((company) => <TableRow key={company.id}><TableCell><Link className="font-medium text-brand-700 hover:underline" to={`/companies/${company.id}`}>{company.name}</Link></TableCell><TableCell>{company.domain || '—'}</TableCell><TableCell>{company._count?.users ?? 0}</TableCell><TableCell>{company._count?.tickets ?? 0}</TableCell></TableRow>)}</TableBody></Table>}</CardContent></Card></div></div>;
}

export function CompanyDetailPage() {
  const { t, tOr } = useI18n();
  const { confirm } = useConfirm();
  const { id } = useParams<{ id: string }>(); const navigate = useNavigate(); const [company, setCompany] = React.useState<Company | null>(null); const [loading, setLoading] = React.useState(true); const [error, setError] = React.useState('');
  const load = React.useCallback(async () => { if (!id) return; setLoading(true); setError(''); try { setCompany(await api.get<Company>(`/companies/${id}`)); } catch (e) { setError(errorMessage(e, t)); } finally { setLoading(false); } }, [id, t]); React.useEffect(() => { void load(); }, [load]);
  async function remove() { if (!id || !await confirm({ title: t('dir.companies.confirmDeleteTitle', { name: company?.name ?? t('dir.companies.thisCompany') }), description: t('dir.companies.confirmDeleteDescription'), confirmLabel: t('dir.companies.delete') })) return; try { await api.delete(`/companies/${id}`); navigate('/companies'); } catch (e) { setError(errorMessage(e, t)); } }
  if (loading) return <div className="space-y-6"><SkeletonText lines={2} className="max-w-sm" /><div className="grid gap-6 lg:grid-cols-2"><Card><CardContent className="p-6"><SkeletonText lines={4} /></CardContent></Card><Card><CardContent className="p-6"><SkeletonText lines={4} /></CardContent></Card></div></div>; if (error || !company) return <PageError message={error || t('dir.companies.notFound')} onRetry={load} />;
  return <div className="space-y-6"><PageHeader className="mb-0" breadcrumb={[{ label: t('nav.companies'), to: '/companies' }, { label: company.name }]} title={company.name} description={company.domain || t('dir.companies.noDomain')} actions={<Button variant="destructive" onClick={() => void remove()}>{t('common.delete')}</Button>} /><div className="grid gap-6 lg:grid-cols-2"><Card><CardHeader><CardTitle>{t('dir.companies.contactsCount', { count: company.users?.length ?? 0 })}</CardTitle></CardHeader><CardContent>{!company.users?.length ? <EmptyState title={t('dir.contacts.none')} description={t('dir.companies.noContactsDescription')} /> : <ul className="space-y-3 text-sm">{company.users.map((user) => <li key={user.id}><Link className="text-brand-700 hover:underline" to={`/contacts/${user.id}`}>{user.firstName} {user.lastName}</Link><span className="ml-2 text-ink-subtle">{user.email}</span></li>)}</ul>}</CardContent></Card><Card><CardHeader><CardTitle>{t('dir.companies.ticketsCount', { count: company.tickets?.length ?? 0 })}</CardTitle></CardHeader><CardContent>{!company.tickets?.length ? <EmptyState title={t('dir.noTicketsYet')} description={t('dir.companies.noTicketsDescription')} /> : <ul className="space-y-3 text-sm">{company.tickets.map((ticket) => <li key={ticket.id}><Link className="text-brand-700 hover:underline" to={`/tickets/${ticket.id}`}>#{ticket.ticketNumber} {ticket.subject}</Link><span className="ml-2 capitalize text-ink-subtle">{localizeLabel('status', ticket.status, resolveStatus(ticket.status).label, tOr)}</span></li>)}</ul>}</CardContent></Card></div></div>;
}

import { RecentActivitiesButton } from '@/components/dashboard/recent-activities-panel';

type Stats = { total: number; open: number; pending: number; resolved: number; unassigned: number; overdue: number; dueToday: number };
// Compatibility helper for the legacy dashboard export.
function MetricCard(props: { label: string; value: React.ReactNode }) {
  return <Card><CardContent><span>{props.label}</span><strong>{props.value}</strong></CardContent></Card>;
}
type HourlyChart = { today: number[]; yesterday: number[] };

/**
 * Dashboard, restyled to match the captured Freshdesk reference
 * (image5.png–image8.png in .kiro/specs/docx-screenshots/media):
 *   - "Today's trends" hourly line chart comparing Today vs Yesterday
 *     (not a 14-day bar chart — the bar chart was this project's own
 *     invention and didn't match the reference).
 *   - Stat cards renamed to the Freshdesk set: Unresolved / Overdue /
 *     Due today / Open / On hold / Unassigned.
 *
 * "Average First response time" and "Resolution within SLA" stat blocks
 * from the reference are intentionally NOT built here: SLA due-date fields
 * (resolutionDueBy/responseDueBy) don't exist yet (P8-SLA, not started per
 * project state), so there's no real data to show. Fabricating placeholder
 * numbers would be worse than omitting the blocks.
 *
 * The per-group widget grid from the reference (Unresolved tickets by
 * group, Undelivered emails by group, Customer satisfaction %, Available
 * agents, Forums, To-do) maps to the already-built-but-unwired `/dashboards`
 * custom-widget backend (DashboardsController) — that's a separate, larger
 * effort (building a widget-grid editor) and is tracked, not done here.
 */
function useHourlyChart(groupId: string) {
  const { t } = useI18n();
  const [chart, setChart] = React.useState<HourlyChart>({ today: new Array(24).fill(0), yesterday: new Array(24).fill(0) });
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<{ data: HourlyChart }>('/dashboard/chart', { params: { mode: 'hourly', ...(groupId ? { groupId } : {}) } });
      setChart(response.data);
    } catch (e) {
      setError(errorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }, [groupId, t]);
  React.useEffect(() => { void load(); }, [load]);
  return { chart, error, loading, reload: load };
}

function TrendsChart({ chart }: { chart: HourlyChart }) {
  const { t } = useI18n();
  const width = 900;
  const height = 180;
  const hours = 24;
  const max = Math.max(...chart.today, ...chart.yesterday, 1);
  const stepX = width / (hours - 1);
  const toPoints = (series: number[]) =>
    series.map((value, hour) => `${hour * stepX},${height - (value / max) * (height - 20)}`).join(' ');

  return (
    <div className="space-y-3">
      <svg viewBox={`0 0 ${width} ${height + 24}`} className="h-48 w-full" role="img" aria-label={t('dashboard.chartAria')}>
        <polyline points={toPoints(chart.yesterday)} fill="none" stroke="#7CACF8" strokeWidth={2} />
        <polyline points={toPoints(chart.today)} fill="none" stroke="#00368F" strokeWidth={2.5} />
        {chart.today.map((value, hour) => (
          <circle key={`today-${hour}`} cx={hour * stepX} cy={height - (value / max) * (height - 20)} r={3} fill="#00368F" />
        ))}
        {[0, 4, 8, 12, 16, 20, 23].map((hour) => (
          <text key={hour} x={hour * stepX} y={height + 18} fontSize={10} fill="#8A93A6" textAnchor="middle">
            {hour}
          </text>
        ))}
      </svg>
      <div className="flex items-center gap-4 text-xs text-ink-subtle">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-brand-600" /> {t('dashboard.today')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-brand-200" /> {t('dashboard.yesterday')}
        </span>
        <span className="ml-auto">{t('dir.trendsCreatedHour')}</span>
      </div>
    </div>
  );
}

export function LegacyDashboardPage() {
  const { t } = useI18n();
  const [stats, setStats] = React.useState<Stats | null>(null);
  const [groups, setGroups] = React.useState<{ value: string; label: string }[]>([]);
  const [groupId, setGroupId] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const { chart, loading: chartLoading, reload: reloadChart } = useHourlyChart(groupId);

  React.useEffect(() => {
    api.get<{ data: { groups: { value: string; label: string }[] } }>('/dropdown-options/batch')
      .then((response) => setGroups(response.data.groups))
      .catch(() => setGroups([]));
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = groupId ? { groupId } : undefined;
      const statsResponse = await api.get<{ data: Stats }>('/dashboard/stats', { params });
      setStats(statsResponse.data);
    } catch (e) {
      setError(errorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }, [groupId, t]);
  React.useEffect(() => { void load(); }, [load]);

  function retryAll() {
    void load();
    void reloadChart();
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => <SkeletonCard key={index} />)}
        </div>
        <Card className="border-line shadow-card"><CardContent className="p-5"><Skeleton className="h-48 w-full" /></CardContent></Card>
      </div>
    );
  }
  if (error) return <PageError message={error} onRetry={retryAll} />;

  const cards: Array<[string, number | undefined, string]> = [
    [t('dashboard.unresolved'), stats ? stats.open + stats.pending : undefined, 'status=open,pending'],
    [t('dashboard.overdue'), stats?.overdue, 'due=overdue'],
    [t('dashboard.dueToday'), stats?.dueToday, 'due=due_today'],
    [t('dashboard.open'), stats?.open, 'status=open'],
    [t('dashboard.onHold'), stats?.pending, 'status=pending'],
    [t('dashboard.unassigned'), stats?.unassigned, 'unassigned=true'],
  ];
  const groupOptions = [{ value: '', label: t('dashboard.allGroups') }, ...groups];

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        className="mb-0"
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        actions={(
          <>
            <div className="w-56">
              <DropdownSelect options={groupOptions} value={groupId} onChange={setGroupId} placeholder={t('dashboard.allGroups')} />
            </div>
            <RecentActivitiesButton groupId={groupId || undefined} />
          </>
        )}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {cards.map(([label, value, filter]) => (
          <Link key={label} to={`/tickets?${filter}`} className="block rounded-lg transition hover:-translate-y-0.5 hover:shadow-raised focus:outline-none focus:ring-2 focus:ring-brand-600">
            <MetricCard label={label} value={value ?? 0} />
          </Link>
        ))}
      </div>
      <Card className="border-line shadow-card">
        <CardHeader><CardTitle className="text-ink">{t('dashboard.trends')}</CardTitle></CardHeader>
        <CardContent>{chartLoading ? <Skeleton className="h-48 w-full" /> : <TrendsChart chart={chart} />}</CardContent>
      </Card>
    </div>
  );
}
